// Funnel.jsx
import { useEffect, useState, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { funnels } from "../data/funnels";

export default function Funnel({ funnelName, onSubmit }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [token, setToken] = useState(null);
  const captchaRef = useRef(null);
  const funnelRef = useRef(null); // 🔹
  
  const funnelData = funnels[funnelName];
  const shared = funnelData.shared || [];
  const personal = funnelData.personal || [];
  const business = funnelData.business || [];

  let allQuestions = [];
  
  if (shared.length || personal.length || business.length) {
    const branch = answers.personalOrBusiness;
    if (shared.length > 0 && (personal.length > 0 || business.length > 0)) {
      allQuestions = [...shared, ...(branch === "Personal" ? personal : branch === "Business" ? business : [])];
    } else {
      allQuestions = personal; // fallback for non-branching funnels like Final Expenses
    }
  }

  const visibleQuestions = allQuestions.filter(q => !q.condition || q.condition(answers));
  const q = step < visibleQuestions.length ? visibleQuestions[step] : null;

  // 🔹 Scroll into view when funnel mounts or funnelName changes
  useEffect(() => {
    funnelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [funnelName]);



  const next = () => {
    if (!answers[q.key]) return alert("Please answer before continuing.");
    setStep(step + 1);
  };

  const prev = () => setStep(Math.max(step - 1, 0));

	const handleSubmit = async () => {
	  if (!token) {
		captchaRef.current?.execute();
		return alert("Please complete the CAPTCHA.");
	  }

	  try {
		  const response = await fetch("/api/contact.php", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
			  source: funnelName.toLowerCase().replace(/\s+/g, '_') + "_funnel",
				  pageUrl: window.location.href,
				  referrer: document.referrer || "",
			  ...answers,
			  hcaptchaToken: token 
			})
		  });

		  const text = await response.text();
		  let result;
		  try {
			result = JSON.parse(text);
		  } catch (err) {
			console.error("Invalid JSON response from server:", text);
			alert("Submission failed. Server returned invalid response.");
			return;
		  }

		  if (!result.success) {
			alert("Submission error: " + (result.error || "Unknown error"));
			return;
		  }

		  alert("Thank you! A representative will reach out shortly. Message and data rates may apply.");

		  setTimeout(() => {
			onSubmit({ answers });
		  }, 100);

	} catch (err) {
	  console.error("Submission error:", err);
	  alert("An error occurred during submission.");
	}
	};

  return (
    <div ref={funnelRef} className="max-w-md mx-auto p-6 bg-gray-800 text-white space-y-6 rounded-lg">
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">{funnelName} Questionnaire</h2>
        <div className="w-full bg-gray-600 rounded-full h-3">
          <div
            className="bg-cyan-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${Math.round(((step + 1) / visibleQuestions.length) * 100)}%` }}
          />
        </div>
      </div>
      <div>
        {q ? (
          <>
            <p className="mb-2">{q.text}</p>

            {q.type === "choice" && q.options.map(opt => (
              <button
                key={opt}
                className={`block mb-2 px-4 py-2 rounded ${answers[q.key] === opt ? "bg-cyan-500" : "bg-gray-700"}`}
                onClick={() => setAnswers({ ...answers, [q.key]: opt })}
              >
                {opt}
              </button>
            ))}

            {(q.type === "text" || q.type === "number") && (
              <input
                type={q.type}
                className="w-full p-3 rounded bg-gray-700"
                value={answers[q.key] || ""}
                onChange={e => setAnswers({ ...answers, [q.key]: e.target.value })}
              />
            )}

            <div className="flex justify-between mt-4">
              <button onClick={prev} disabled={step === 0} className="bg-gray-600 px-4 py-2 rounded">Back</button>
              <button onClick={next} className="bg-cyan-500 px-4 py-2 rounded">Next</button>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full p-3 rounded bg-gray-700"
                value={answers.fullName || ""}
                onChange={e => setAnswers({ ...answers, fullName: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full p-3 rounded bg-gray-700"
                value={answers.email || ""}
                onChange={e => setAnswers({ ...answers, email: e.target.value })}
                required
              />
              <input
                type="tel"
                placeholder="Your Phone"
                className="w-full p-3 rounded bg-gray-700"
                value={answers.phone || ""}
                onChange={e => setAnswers({ ...answers, phone: e.target.value })}
                required
              />
            </div>

            <div className="bg-gray-700 p-4 rounded">
              <HCaptcha
                sitekey="13e03332-fc3e-4fac-aa46-db4a449a48ba"
                onVerify={setToken}
                ref={captchaRef}
              />
            </div>

            <div className="flex justify-between mt-4">
              <button onClick={prev} className="bg-gray-600 px-4 py-2 rounded">Back</button>
              <button onClick={handleSubmit} className="bg-green-500 px-6 py-2 rounded text-white font-semibold hover:bg-green-600">Submit</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
